import { useState } from "react";
import { Link, useSearch } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import { AuthShell } from "@/components/AuthShell";
import { FormError } from "@/components/FormError";
import { authApi } from "@/api/endpoints/auth";
import { ApiError } from "@/api/client";
import { translateError } from "@/i18n/errors";

const schema = z.object({
  password: z.string().min(12, "Password must be at least 12 characters."),
});
type FormValues = z.infer<typeof schema>;

export function ResetPasswordPage(): JSX.Element {
  const search = useSearch({ strict: false }) as { token?: string };
  const [done, setDone] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>();

  const mutation = useMutation({
    mutationFn: (v: FormValues) => {
      if (!search.token) throw new Error("missing");
      return authApi.resetPassword({ token: search.token, new_password: v.password });
    },
    onSuccess: () => setDone(true),
    onError: (err) => {
      if (err instanceof ApiError) setServerError(translateError(err.code, err.message));
      else setServerError(translateError("AUTH_TOKEN_INVALID"));
    },
  });

  if (done) {
    return (
      <AuthShell sideTitle="Password reset.">
        <div>
          <h1>Password updated</h1>
          <p>You can now sign in with your new password.</p>
        </div>
        <Link to="/login" className="btn btn-primary btn-lg btn-block">
          Sign in
        </Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell sideTitle="Set a new password.">
      <div>
        <h1>Choose a new password</h1>
        <p>Use 12+ characters with letters and a number.</p>
      </div>
      <form onSubmit={handleSubmit((v) => { setServerError(null); mutation.mutate(v); })} noValidate>
        <div className="field-group">
          <label htmlFor="password">New password</label>
          <input
            id="password"
            type="password"
            autoComplete="new-password"
            className="field-input"
            data-testid="reset-password"
            {...register("password", { required: "Password is required." })}
          />
          <FormError message={errors.password?.message} />
        </div>
        <FormError message={serverError} />
        <button
          type="submit"
          className="btn btn-primary btn-lg btn-block"
          disabled={mutation.isPending}
        >
          {mutation.isPending ? "Saving…" : "Update password"}
        </button>
      </form>
    </AuthShell>
  );
}
